import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { AuthService } from "@core/services/auth.service";
import { HttpRetryService } from "@core/services/http-retry.service";
import { LoadingService } from "@core/services/loading.service";
import { PopNotificationsService } from "@core/services/pop-notifications.service";
import { UtilsService } from "@core/services/utils/utils.service";
import { WindowRefService } from "@core/services/window-ref.service";
import { TranslateService } from "@ngx-translate/core";
import { RouteMappingService } from "@shared/api-obfuscation/services/route-mapping.service";
import { Observable, throwError } from "rxjs";
import { catchError, retry } from "rxjs/operators";

export class ServerErrorsInterceptor implements HttpInterceptor {
  private readonly ignored401Paths = [
    /.*\/api\/v2\/auth\/login\/$/,
    /.*\/api\/v2\/auth\/verify-2fa\/$/,
    /.*\/api\/v2\/auth\/register\/$/,
    /.*\/api\/v2\/auth\/activate\/$/,
    /.*\/api\/v2\/auth\/resend-activation\/$/,
    /.*\/api\/v2\/auth\/password-reset\/$/,
    /.*\/api\/v2\/auth\/password-reset\/confirm\/$/,
    /.*\/api\/v2\/auth\/password-reset\/validate-token\/$/,
    /.*\/api\/v2\/auth\/resend-2fa-code\/$/,
    /.*\/api\/v2\/auth\/2fa-status\/$/,
    /.*\/api\/v2\/auth\/logout\/$/
  ];

  private readonly ignored404Paths = [
    /.*\/api\/v2\/equipment\/\w+\/\d+\/release-reviewer-lock\/$/,
    /.*\/json-api\/common\/ckeditor-upload\/*/,
    /.*\/json-api\/common\/editor-upload\/*/,
    /.*\/api\/v2\/images\/image-search\/\?params=.*/
  ];

  private readonly ignoredAnyErrorPaths = [
    /.*\/json-api\/common\/service-worker-control\/$/,
    /.*\/json-api\/common\/record-hit\/*/,
    /.*\/json-api\/common\/editor-upload\/*/
  ];

  private readonly ignored429Paths = [
    /.*\/api\/v2\/auth\/register\/$/,
    /.*\/api\/v2\/auth\/resend-activation\/$/,
    /.*\/api\/v2\/forum\/similar-topics\/search\/$/,
    /.*\/api\/v2\/forum\/tag-generation\/$/,
    /.*\/api\/v2\/forum\/title-generation\/$/
  ];

  constructor(
    public readonly windowRefService: WindowRefService,
    public readonly translateService: TranslateService,
    public readonly popNotificationsService: PopNotificationsService,
    public readonly loadingService: LoadingService,
    public readonly authService: AuthService,
    public readonly utilsService: UtilsService,
    private httpRetryService: HttpRetryService,
    public readonly routeMappingService: RouteMappingService
  ) {}

  public intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // Check if running in a browser environment
    if (typeof window === "undefined") {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      retry(1),
      catchError(returnedError => {
        if (this.handleError(returnedError)) {
          return throwError(returnedError);
        }

        return next.handle(request);
      })
    );
  }

  public handleError(err: HttpErrorResponse) {
    // For status 0 errors, check if it's being handled by TimeoutRetryInterceptor
    if (err.status === 0 && err.url && this.httpRetryService.isRetryInProgress(err.url)) {
      return false;
    }

    let errorTitle: string;
    let errorMessage: string;
    let handled = false;

    if (this.ignoredAnyErrorPaths.some(regex => regex.test(err.url))) {
      return false;
    }

    // The backend returned an unsuccessful response code.
    switch (err.status) {
      case 0:
        errorTitle = this.translateService.instant("Connection error");
        errorMessage = this.translateService.instant(
          "Could not connect to the server. Please check your internet connection and try again."
        );
        break;
      case 400:
        errorTitle = this.translateService.instant("Bad request");

        if (UtilsService.isString(err.error)) {
          errorMessage = err.error;
        } else if (Array.isArray(err.error)) {
          errorMessage = "";
          for (const error of err.error) {
            errorMessage += `${error}<br/>`;
          }
        } else if (UtilsService.isObject(err.error)) {
          errorMessage = "";

          // Helper function to format nested objects/arrays recursively
          const formatValue = (value: any, indent: string = ""): string => {
            let result = "";

            if (Array.isArray(value)) {
              value.forEach(item => {
                if (typeof item === "string" || typeof item === "number" || typeof item === "boolean") {
                  result += `${indent}• ${item}<br/>`;
                } else if (UtilsService.isObject(item)) {
                  result += `${indent}• ${formatValue(item, indent + "  ")}`;
                } else {
                  result += `${indent}• ${JSON.stringify(item)}<br/>`;
                }
              });
            } else if (UtilsService.isObject(value)) {
              for (const subKey of Object.keys(value)) {
                const subValue = value[subKey];
                result += `${indent}<strong>${subKey}</strong>:<br/>`;
                result += formatValue(subValue, indent + "  ");
              }
            } else {
              result += `${indent}${value}<br/>`;
            }

            return result;
          };

          // Process top-level keys
          for (const key of Object.keys(err.error)) {
            const value = err.error[key];
            errorMessage += `<strong>${key}</strong>:<br/>`;
            errorMessage += formatValue(value, "  ");
          }
        }

        if (!!errorMessage && errorMessage.indexOf("AstroBin stands with Ukraine") > -1) {
          this.windowRefService.nativeWindow.location.assign(
            "https://welcome.astrobin.com/astrobin-stands-with-ukraine"
          );
        }

        break;
      case 401:
        // Don't handle 401 errors for authentication endpoints - they should handle their own errors
        if (this.shouldIgnore401Error(err.url)) {
          return false;
        }

        if (err.error?.detail === "Invalid token.") {
          errorTitle = this.translateService.instant("Invalid token");
          errorMessage = this.translateService.instant(
            "The authentication token you provided is invalid. Please log in again."
          );
          this.authService.removeAuthenticationToken();
        } else {
          errorTitle = this.translateService.instant("Login required");
          errorMessage = this.translateService.instant(
            "You tried to access a server resource that's only available to logged in users."
          );

          // Force logout in case of invalid authentication token.
          this.authService.logout();
          this.utilsService.delay(3000).subscribe(() => {
            this.windowRefService.nativeWindow.location.assign("/account/login");
          });
        }

        break;
      case 403:
        errorTitle = this.translateService.instant("Permission denied");

        if (UtilsService.isString(err.error)) {
          errorMessage = err.error;
        } else if (UtilsService.isObject(err.error) && err.error.detail) {
          errorMessage = err.error.detail;
        } else {
          errorMessage = this.translateService.instant(
            "You tried to access a server resource that you're not authorized for."
          );
        }
        break;
      case 404:
        let ignore = false;
        for (const ignoredPathRegex of this.ignored404Paths) {
          if (ignoredPathRegex.test(err.url)) {
            ignore = true;
          }
        }

        if (!ignore) {
          errorTitle = this.translateService.instant("The requested resource does not exist");
          errorMessage = err.url;
        }

        break;
      case 409:
        errorTitle = this.translateService.instant("Conflict detected");
        errorMessage = err.error || err.url;
        break;
      case 429:
        // Don't show global error for certain endpoints that handle their own rate limiting
        if (this.shouldIgnore429Error(err.url)) {
          return false;
        }
        errorTitle = this.translateService.instant("Too many requests");
        errorMessage =
          this.translateService.instant(
            "You're trying to do this operation too many times in a short period of time."
          ) +
          " " +
          this.translateService.instant("For security reasons, this is not permitted.");
        break;
      case 500:
        errorTitle = this.translateService.instant("Internal server error");
        errorMessage = this.translateService.instant(
          "This should never happen. AstroBin's staff has been notified automatically and will solve the " +
            "problem as soon as possible."
        );
        break;
      case 503:
        errorTitle = this.translateService.instant("Service not available");
        errorMessage = this.translateService.instant(
          "This should never happen. If the problem persists, please contact us."
        );
        break;
      case 422:
        errorTitle = this.translateService.instant("Validation error");
        errorMessage = err.error;
        break;
      default:
        errorTitle = this.translateService.instant("Something went wrong") + ` (error ${err.status})`;
        errorMessage =
          this.translateService.instant("If you can reproduce this issue reliably, please contact us.") +
          ` (error: ${err.message}`;
    }

    if (errorTitle && errorMessage) {
      this.popNotificationsService.error(errorMessage, errorTitle, {
        timeOut: 60000,
        disableTimeOut: "extendedTimeOut",
        enableHtml: true,
        closeButton: true
      });

      this.loadingService.setLoading(false);

      handled = true;
    }

    return handled;
  }

  /**
   * Determines if a 401 error should be ignored (not trigger error handling)
   * @param url The URL that returned the 401 error
   * @returns true if the error should be ignored, false otherwise
   */
  shouldIgnore401Error(url: string): boolean {
    // Check if URL matches any of the ignored auth endpoint patterns
    let shouldIgnore = this.ignored401Paths.some(regex => regex.test(url));

    // If not matched yet, check if this is an obfuscated auth URL
    if (!shouldIgnore) {
      // Get all obfuscated URLs for auth endpoints
      const obfuscatedAuthUrls: string[] = [];
      for (const pattern of this.ignored401Paths) {
        const obfuscatedUrls = this.routeMappingService.getObfuscatedUrlsForPattern(pattern);
        obfuscatedAuthUrls.push(...obfuscatedUrls);
      }

      // Check if the error URL matches any obfuscated auth URL
      shouldIgnore = obfuscatedAuthUrls.some(obfuscatedUrl => {
        // Extract path from url if needed
        let errorPath = url;
        try {
          const urlObj = new URL(url);
          errorPath = urlObj.pathname;
        } catch {
          // Not a valid URL, assume it's already a path
        }
        return errorPath.includes(obfuscatedUrl);
      });
    }

    return shouldIgnore;
  }

  /**
   * Determines if a 429 error should be ignored (not trigger error handling)
   * @param url The URL that returned the 429 error
   * @returns true if the error should be ignored, false otherwise
   */
  shouldIgnore429Error(url: string): boolean {
    // Check if URL matches any of the ignored 429 endpoint patterns
    let shouldIgnore = this.ignored429Paths.some(regex => regex.test(url));

    // If not matched yet, check if this is an obfuscated URL
    if (!shouldIgnore) {
      // Get all obfuscated URLs for 429 endpoints
      const obfuscatedUrls: string[] = [];
      for (const pattern of this.ignored429Paths) {
        const obfuscated = this.routeMappingService.getObfuscatedUrlsForPattern(pattern);
        obfuscatedUrls.push(...obfuscated);
      }

      // Check if the error URL matches any obfuscated URL
      shouldIgnore = obfuscatedUrls.some(obfuscatedUrl => {
        // Extract path from url if needed
        let errorPath = url;
        try {
          const urlObj = new URL(url);
          errorPath = urlObj.pathname;
        } catch {
          // Not a valid URL, assume it's already a path
        }
        return errorPath.includes(obfuscatedUrl);
      });
    }

    return shouldIgnore;
  }
}
